import React, { useState, useEffect } from 'react';
import './AlertNotification.css';

const AlertNotification = ({ 
    alert, 
    onClose, 
    onViewToken,
    autoClose = true,
    duration = 8000 
}) => {
    const [isVisible, setIsVisible] = useState(true);
    const [timeLeft, setTimeLeft] = useState(duration / 1000);

    useEffect(() => {
        if (!autoClose) return;

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    handleClose();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [autoClose, duration]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => {
            onClose && onClose(alert.alertId);
        }, 300); // Wait for animation
    };

    const handleViewToken = () => {
        onViewToken && onViewToken(alert.tokenAddress);
        handleClose();
    };

    const getAlertIcon = () => {
        switch (alert.alertType) {
            case 'price_above':
                return '🚀';
            case 'price_below':
                return '📉';
            case 'percentage_change':
                return '📊';
            default:
                return '🔔';
        }
    };

    const getAlertTitle = () => {
        switch (alert.alertType) {
            case 'price_above':
                return 'Price Target Reached';
            case 'price_below':
                return 'Price Drop Alert';
            case 'percentage_change':
                return 'Price Change Alert';
            default:
                return 'Price Alert';
        }
    };

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return `$${parseFloat(price).toFixed(6)}`;
    };

    const getAlertMessage = () => {
        const { tokenSymbol, alertType, currentPrice, targetPrice, percentageThreshold } = alert;

        switch (alertType) {
            case 'price_above':
                return `${tokenSymbol} reached ${formatPrice(currentPrice)} (target: ${formatPrice(targetPrice)})`;
            case 'price_below':
                return `${tokenSymbol} dropped to ${formatPrice(currentPrice)} (target: ${formatPrice(targetPrice)})`;
            case 'percentage_change':
                return `${tokenSymbol} changed by ${percentageThreshold}% - now ${formatPrice(currentPrice)}`;
            default:
                return alert.triggerReason || 'Price alert triggered';
        }
    };

    if (!isVisible) return null;

    return (
        <div className={`alert-notification ${isVisible ? 'visible' : 'hidden'}`}>
            <div className="alert-content">
                <div className="alert-header">
                    <div className="alert-icon">{getAlertIcon()}</div>
                    <div className="alert-title-section">
                        <h4 className="alert-title">{getAlertTitle()}</h4>
                        <div className="token-info">
                            <span className="token-symbol">{alert.tokenSymbol}</span>
                            {alert.tokenName && (
                                <span className="token-name">({alert.tokenName})</span>
                            )}
                        </div>
                    </div>
                    {autoClose && (
                        <div className="auto-close-timer">
                            <span className="timer-text">{timeLeft}s</span>
                            <div className="timer-circle">
                                <svg width="24" height="24" viewBox="0 0 24 24">
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeDasharray={`${(timeLeft / (duration / 1000)) * 62.83} 62.83`}
                                        transform="rotate(-90 12 12)"
                                    />
                                </svg>
                            </div>
                        </div>
                    )}
                </div>

                <div className="alert-message">
                    {getAlertMessage()}
                </div>

                <div className="alert-details">
                    <div className="detail-item">
                        <span className="detail-label">Current Price:</span>
                        <span className="detail-value price">{formatPrice(alert.currentPrice)}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-label">Time:</span>
                        <span className="detail-value">
                            {new Date(alert.timestamp).toLocaleTimeString()}
                        </span>
                    </div>
                </div>

                <div className="alert-actions">
                    <button 
                        className="view-token-btn"
                        onClick={handleViewToken}
                    >
                        View Token
                    </button>
                    <button 
                        className="dismiss-btn"
                        onClick={handleClose}
                    >
                        Dismiss
                    </button>
                </div>
            </div>

            <button 
                className="close-btn"
                onClick={handleClose}
                aria-label="Close notification"
            >
                ×
            </button>
        </div>
    );
};

export default AlertNotification;